"use client";
import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { ActionType } from '@/types/Common.types';

// Types for the save functions
type SaveFunction = (e: React.MouseEvent<HTMLButtonElement>, actionType?: ActionType) => Promise<void>;

interface TabSaveContextType {
  // Register save functions for each tab
  registerSaveFunction: (tabIndex: number, saveFunction: SaveFunction) => void;
  // Unregister save function when component unmounts
  unregisterSaveFunction: (tabIndex: number) => void;
  // Get save function for a specific tab
  getSaveFunction: (tabIndex: number) => SaveFunction | null;
  // Execute save for a specific tab
  saveTab: (tabIndex: number) => Promise<void>;
  // Register change tracking for a tab
  registerChangeTracker: (tabIndex: number, hasChanges: () => boolean) => void;
  // Unregister change tracker when component unmounts
  unregisterChangeTracker: (tabIndex: number) => void;
  // Check if a tab has unsaved changes
  hasUnsavedChanges: (tabIndex: number) => boolean;
}

const TabSaveContext = createContext<TabSaveContextType | undefined>(undefined);

interface TabSaveProviderProps {
  children: ReactNode;
}

export const TabSaveProvider: React.FC<TabSaveProviderProps> = ({ children }) => {
  // Store save functions for each tab
  const saveFunctions = React.useRef<Map<number, SaveFunction>>(new Map());
  // Store change trackers for each tab
  const changeTrackers = React.useRef<Map<number, () => boolean>>(new Map());

  const registerSaveFunction = useCallback((tabIndex: number, saveFunction: SaveFunction) => {
    saveFunctions.current.set(tabIndex, saveFunction);
  }, []);

  const unregisterSaveFunction = useCallback((tabIndex: number) => {
    saveFunctions.current.delete(tabIndex);
  }, []);

  const getSaveFunction = useCallback((tabIndex: number): SaveFunction | null => {
    return saveFunctions.current.get(tabIndex) || null;
  }, []);

  const registerChangeTracker = useCallback((tabIndex: number, hasChanges: () => boolean) => {
    changeTrackers.current.set(tabIndex, hasChanges);
  }, []);

  const unregisterChangeTracker = useCallback((tabIndex: number) => {
    changeTrackers.current.delete(tabIndex);
  }, []);

  const hasUnsavedChanges = useCallback((tabIndex: number): boolean => {
    const changeTracker = changeTrackers.current.get(tabIndex);
    return changeTracker ? changeTracker() : false;
  }, []);

  const saveTab = useCallback(async (tabIndex: number): Promise<void> => {
    // Check if there are unsaved changes
    const hasChanges = hasUnsavedChanges(tabIndex);    
    if (!hasChanges) {
      return;
    }

    const saveFunction = saveFunctions.current.get(tabIndex);
    if (saveFunction) {
      // Create a mock event for auto-save (not a click event)
      const mockEvent = { 
        preventDefault: () => {},
        type: 'auto-save' // This will indicate it's not a manual save
      } as React.MouseEvent<HTMLButtonElement>;
      await saveFunction(mockEvent, "save");
    } else {
    }
  }, [hasUnsavedChanges]);

  const value: TabSaveContextType = React.useMemo(() => ({
    registerSaveFunction,
    unregisterSaveFunction,
    getSaveFunction,
    saveTab,
    registerChangeTracker,
    unregisterChangeTracker,
    hasUnsavedChanges,
  }), [registerSaveFunction, unregisterSaveFunction, getSaveFunction, saveTab, registerChangeTracker, unregisterChangeTracker, hasUnsavedChanges]);

  return (
    <TabSaveContext.Provider value={value}>
      {children}
    </TabSaveContext.Provider>
  );
};

// Custom hook to use the TabSave context
export const useTabSave = (): TabSaveContextType => {
  const context = useContext(TabSaveContext);
  if (context === undefined) {
    throw new Error('useTabSave must be used within a TabSaveProvider');
  }
  return context;
};

export default TabSaveContext;
